import React from 'react';
import { CheckCircle2, CreditCard, IndianRupee, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import WalkinSection from './WalkinSection';

export default function WalkinFeeSection({ form }) {
  const {
    fee, setFee, feeTier, feeNum, payMethod, setPayMethod, payRef, setPayRef,
    paying, showDiscount, setShowDiscount, discKind, setDiscKind,
    discValue, setDiscValue, discReason, setDiscReason, discNote, setDiscNote,
    adminPin, setAdminPin, discCfg, discPreview, rawDisc,
    mode, selectedExisting, checkin,
  } = form;

  const collect = showDiscount && rawDisc > 0 ? discPreview.final : feeNum;
  const waived = showDiscount && rawDisc > 0 && discPreview.final === 0;
  const namePart = mode === 'pick' && selectedExisting
    ? `Check in ${selectedExisting.name}`
    : 'Register walk-in';

  return (
    <WalkinSection
      step={4}
      title="Fee & payment"
      hint="Collect consultation fee now. Queue number is issued after triage and emailed to the patient."
      testId="walkin-fee-section"
    >
      <div className="space-y-1.5">
        <Label className="flex items-center gap-1.5">
          <IndianRupee className="h-3 w-3" /> Consultation fee
          {feeTier && (
            <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full border ${
              feeTier === 'first_visit' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : feeTier === 'followup' ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : 'bg-zinc-50 text-zinc-600 border-zinc-200'
            }`} data-testid="rec-fee-tier">
              {feeTier === 'first_visit' ? 'First visit' : feeTier === 'followup' ? 'Follow-up' : 'Walk-in / repeat'}
            </span>
          )}
        </Label>
        <Input value={fee} onChange={(e) => setFee(e.target.value)} type="number" data-testid="rec-fee" />
      </div>

      <div className="border border-dashed border-[#D5DEE8] rounded-xl px-3 py-2.5 bg-[#F8FAFC]">
        {!showDiscount ? (
          <button type="button" onClick={() => setShowDiscount(true)}
            className="text-[11px] font-semibold text-[#F15A22] hover:underline inline-flex items-center gap-1.5"
            data-testid="rec-discount-open">
            <IndianRupee className="h-3 w-3" /> Add fee exception / discount
          </button>
        ) : (
          <DiscountFields
            discKind={discKind} setDiscKind={setDiscKind}
            discValue={discValue} setDiscValue={setDiscValue}
            discReason={discReason} setDiscReason={setDiscReason}
            discNote={discNote} setDiscNote={setDiscNote}
            adminPin={adminPin} setAdminPin={setAdminPin}
            discCfg={discCfg} discPreview={discPreview} rawDisc={rawDisc}
            onClear={() => {
              setShowDiscount(false); setDiscValue(''); setDiscReason(''); setDiscNote(''); setAdminPin('');
            }}
          />
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5 text-xs">
            <CreditCard className="h-3 w-3" /> Pay method
          </Label>
          <Select value={payMethod} onValueChange={setPayMethod}>
            <SelectTrigger data-testid="rec-pay-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="card">Card</SelectItem>
              <SelectItem value="netbanking">Net banking</SelectItem>
              <SelectItem value="wallet">Wallet</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {payMethod !== 'cash' && (
          <div className="space-y-1.5">
            <Label className="text-xs">Reference / last-4</Label>
            <Input value={payRef} onChange={(e) => setPayRef(e.target.value)}
              placeholder={payMethod === 'upi' ? 'UPI ref #' : payMethod === 'card' ? 'Last-4' : 'Txn ref'}
              data-testid="rec-pay-ref" />
          </div>
        )}
      </div>

      <Button onClick={checkin} disabled={paying || (mode === 'pick' && !selectedExisting)}
        className="w-full h-11 bg-[#F15A22] hover:bg-[#d94e1c] text-white font-semibold"
        data-testid="rec-submit">
        {paying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
        {waived ? `${namePart} · Waived (₹0)` : `${namePart} & collect ₹${collect.toFixed(0)}`}
      </Button>
    </WalkinSection>
  );
}

function DiscountFields({
  discKind, setDiscKind, discValue, setDiscValue, discReason, setDiscReason,
  discNote, setDiscNote, adminPin, setAdminPin, discCfg, discPreview, rawDisc, onClear,
}) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-bold text-[#071B34] uppercase tracking-wide">Fee exception</Label>
        <button type="button" onClick={onClear} className="text-[10px] text-[#5A6B7C] hover:text-[#071B34]" data-testid="rec-discount-close">Clear</button>
      </div>
      <div className="flex items-center gap-1.5 bg-white rounded-md p-0.5 border w-fit">
        <button type="button" onClick={() => setDiscKind('flat')}
          className={`px-2.5 py-1 text-[11px] font-semibold rounded ${discKind === 'flat' ? 'bg-[#F15A22] text-white' : 'text-[#5A6B7C]'}`}
          data-testid="rec-discount-kind-flat">Flat ₹</button>
        <button type="button" onClick={() => setDiscKind('percent')}
          className={`px-2.5 py-1 text-[11px] font-semibold rounded ${discKind === 'percent' ? 'bg-[#F15A22] text-white' : 'text-[#5A6B7C]'}`}
          data-testid="rec-discount-kind-percent">Percent %</button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wide text-[#5A6B7C]">
            {discKind === 'flat' ? 'Discount ₹' : 'Discount %'}
          </Label>
          <Input type="number" value={discValue} onChange={(e) => setDiscValue(e.target.value)}
            placeholder={discKind === 'flat' ? '50' : '20'} data-testid="rec-discount-value" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wide text-[#5A6B7C]">Reason</Label>
          <Select value={discReason} onValueChange={setDiscReason}>
            <SelectTrigger data-testid="rec-discount-reason"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
            <SelectContent>
              {(discCfg.reasons || []).map((r) => (
                <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-[10px] uppercase tracking-wide text-[#5A6B7C]">Note (optional)</Label>
        <Input value={discNote} onChange={(e) => setDiscNote(e.target.value)}
          placeholder="Staff ID, referrer, charity note…" data-testid="rec-discount-note" />
      </div>
      {discPreview.needsPin && (
        <div className="space-y-1 pt-1 border-t border-dashed border-amber-300">
          <Label className="text-[10px] uppercase tracking-wide text-amber-700 font-bold">
            Admin PIN — over {discCfg.approval_threshold_percent}% discount
          </Label>
          <Input type="password" inputMode="numeric" maxLength={8} value={adminPin}
            onChange={(e) => setAdminPin(e.target.value.replace(/\D/g, ''))}
            placeholder="4–8 digit PIN" data-testid="rec-discount-pin" />
        </div>
      )}
      {rawDisc > 0 && (
        <div className="text-[11px] bg-[#FFF7F3] border border-[#F15A22]/30 rounded-md px-2 py-1.5 flex items-center justify-between"
          data-testid="rec-discount-summary">
          <span className="text-[#071B34]"><b>{discPreview.pct}% off</b> · ₹{discPreview.amt.toFixed(0)}</span>
          <span className="font-bold text-[#071B34]">
            Collect ₹{discPreview.final.toFixed(0)}
            {discPreview.final === 0 && <span className="ml-1 text-emerald-700">· Waived</span>}
          </span>
        </div>
      )}
    </div>
  );
}
